import { BadgeLabel, BadgeTier } from './types.js'

interface BadgeTierStyle {
    label: BadgeLabel
    /** Points rule shown in the UI. */
    rule: BadgeLabel
    /** Base metal colour (flat, for dots and chips). */
    metal: string
    /** 5-step light ramp (linear RGB 0..1) for the environment-map filter. */
    ramp: [number, number, number][]
    /** Unlock animation particle colours. */
    spark: string[]
}

export const BADGE_TIER_STYLES: Record<BadgeTier, BadgeTierStyle> = {
    bronze: {
        label: { et: 'Pronks', en: 'Bronze' },
        rule: { et: 'kuni 100 punkti', en: 'up to 100 pts' },
        metal: '#E3A57C',
        ramp: [
            [0.16, 0.06, 0.02],
            [0.4, 0.19, 0.08],
            [0.68, 0.41, 0.23],
            [0.9, 0.66, 0.5],
            [1, 0.9, 0.82],
        ],
        spark: ['#E9A676', '#F8D3B4', '#B7744A'],
    },
    silver: {
        label: { et: 'Hõbe', en: 'Silver' },
        rule: { et: '125 kuni 300 punkti', en: '125 to 300 pts' },
        metal: '#ECEDF0',
        ramp: [
            [0.09, 0.1, 0.13],
            [0.29, 0.31, 0.36],
            [0.56, 0.59, 0.64],
            [0.83, 0.85, 0.88],
            [1, 1, 1],
        ],
        spark: ['#E4E9EF', '#A9B3C1', '#7E8898'],
    },
    gold: {
        label: { et: 'Kuld', en: 'Gold' },
        rule: { et: '350 punkti ja rohkem', en: '350 pts and more' },
        metal: '#F6C766',
        ramp: [
            [0.2, 0.11, 0.02],
            [0.47, 0.29, 0.05],
            [0.78, 0.56, 0.16],
            [0.95, 0.8, 0.44],
            [1, 0.97, 0.84],
        ],
        spark: ['#FFE38A', '#F2B92E', '#FFF6D6'],
    },
    crystal: {
        label: { et: 'Kristall', en: 'Crystal' },
        rule: { et: 'antakse tunnustusena', en: 'awarded as recognition' },
        metal: '#E6EEF7',
        ramp: [
            [0.12, 0.17, 0.26],
            [0.34, 0.44, 0.58],
            [0.62, 0.74, 0.86],
            [0.86, 0.93, 0.99],
            [1, 1, 1],
        ],
        spark: ['#FFFFFF', '#D4ECFF', '#A8D6FA', '#EAF6FF', '#C2E4FF'],
    },
}

export interface TierInput {
    score: number
    anniversaryYears: number | null
    isSpecial: boolean
}

/**
 * Points decide the metal. Special (awarded) badges are always crystal.
 * Anniversaries go by year and stop at gold: 1–4 bronze, 5–9 silver, 10+ gold.
 */
export const tierOf = ({ score, anniversaryYears, isSpecial }: TierInput): BadgeTier => {
    if (isSpecial) return 'crystal'
    if (anniversaryYears !== null) {
        const y = Math.max(1, Math.round(anniversaryYears))
        return y >= 10 ? 'gold' : y >= 5 ? 'silver' : 'bronze'
    }
    return score >= 350 ? 'gold' : score >= 125 ? 'silver' : 'bronze'
}
